import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCardModule } from '@angular/material/card';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTableModule } from '@angular/material/table';
import { MatDividerModule } from '@angular/material/divider';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  PortalAccessTokenService,
  PortalStudent,
  PortalSummary,
  PortalBooking,
  PortalClass,
  PortalTransaction,
  PortalBoxBooking,
  PortalRental,
  AvailableSchool,
} from '../../services/portal-access-token.service';

type State = 'loading' | 'ready' | 'error';
type ClassFilter = 'all' | 'completed' | 'upcoming';

const GENDER_MAP: Record<number, string> = { 1: 'Male', 2: 'Female', 3: 'Other', 4: 'Prefer not to say' };
const STATUS_MAP: Record<number, string> = { 1: 'Invited', 2: 'Active', 3: 'Inactive', 4: 'Accepted' };
const STATUS_CLASS: Record<number, string> = {
  1: 'bg-light-warning text-warning',
  2: 'bg-light-success text-success',
  3: 'bg-light-danger text-danger',
  4: 'bg-light-success text-success',
};
const RELATIONSHIP_MAP: Record<number, string> = {
  1: 'Self',
  2: 'Child',
  3: 'Spouse',
  4: 'Sibling',
  5: 'Friend',
  6: 'Other',
};

@Component({
  selector: 'app-portal-access',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    FormsModule,
    MatIconModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatCardModule,
    MatTabsModule,
    MatTableModule,
    MatDividerModule,
    MatTooltipModule,
  ],
  templateUrl: './portal-access.component.html',
  styleUrl: './portal-access.component.scss',
})
export class PortalAccessComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private portalSvc = inject(PortalAccessTokenService);

  rawToken: string | null = null;
  state: State = 'loading';
  switchingSchool = false;
  classFilter: ClassFilter = 'all';

  student: PortalStudent | null = null;
  school: PortalStudent['school'] | null = null;
  availableSchools: AvailableSchool[] = [];
  selectedSchoolId: string | null = null;

  summary: PortalSummary | null = null;
  breakdown: any = null;
  bookings: PortalBooking[] = [];
  classes: PortalClass[] = [];
  transactions: PortalTransaction[] = [];
  boxBookings: PortalBoxBooking[] = [];
  rentals: PortalRental[] = [];

  // Table columns for ledger
  transactionColumns: string[] = ['createdAt', 'typeName', 'forItem', 'invoiceIdentifier', 'amount'];

  // ─── Computed display values ──────────────────────────────────────────────

  get fullName(): string {
    if (!this.student) return '';
    return [this.student.firstName, this.student.secondName, this.student.thirdName]
      .filter(Boolean)
      .join(' ');
  }

  get initials(): string {
    const name = this.fullName;
    const parts = name.trim().split(/\s+/);
    return parts.length >= 2
      ? `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
      : name.slice(0, 2).toUpperCase();
  }

  get email(): string | null {
    return this.student?.user?.email || null;
  }

  get phone(): string | null {
    return this.student?.phone || this.student?.user?.phone || null;
  }

  get nationality(): string | null {
    return this.student?.user?.nationalityIso || null;
  }

  get address(): string | null {
    return this.student?.user?.address || null;
  }

  get genderLabel(): string | null {
    return this.student?.gender != null ? (GENDER_MAP[this.student.gender] ?? null) : null;
  }

  get relationshipLabel(): string | null {
    return this.student?.relationship != null ? (RELATIONSHIP_MAP[this.student.relationship] ?? null) : null;
  }

  get statusLabel(): string {
    return this.student?.status != null ? (STATUS_MAP[this.student.status] ?? 'Active') : 'Active';
  }

  get statusClass(): string {
    return this.student?.status != null ? (STATUS_CLASS[this.student.status] ?? 'bg-light-success text-success') : 'bg-light-success text-success';
  }

  get joinDate(): string {
    if (!this.student?.createdAt) return '—';
    return new Date(this.student.createdAt).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  }

  get guardianName(): string | null {
    if (!this.student?.guardian) return null;
    return [this.student.guardian.firstName, this.student.guardian.secondName].filter(Boolean).join(' ');
  }

  get filteredClasses(): PortalClass[] {
    if (this.classFilter === 'completed') {
      return this.classes.filter((c) => c.status === 3);
    }
    if (this.classFilter === 'upcoming') {
      return this.classes.filter((c) => c.status === 1 || c.status === 2);
    }
    return this.classes;
  }

  /**
   * Pending balance amounts per currency (matching student-details.component.ts)
   */
  get pendingBalances(): Array<{ currency: string; balance: number }> {
    if (this.breakdown?.byCurrency?.length) {
      return this.breakdown.byCurrency
        .filter((seg: any) => (seg.combined?.balance ?? 0) > 0)
        .map((seg: any) => ({
          currency: seg.currency || '',
          balance: seg.combined.balance ?? 0,
        }));
    }
    if (this.summary?.outstandingBalance && this.summary.outstandingBalance > 0) {
      return [{ currency: this.summary.currency, balance: this.summary.outstandingBalance }];
    }
    return [];
  }

  /**
   * Advance / credit balances per currency (matching student-details.component.ts)
   */
  get creditBalances(): Array<{ currency: string; amount: number }> {
    if (this.breakdown?.byCurrency?.length) {
      return this.breakdown.byCurrency
        .filter((seg: any) => (seg.combined?.balance ?? 0) < 0)
        .map((seg: any) => ({
          currency: seg.currency || '',
          amount: Math.abs(seg.combined.balance ?? 0),
        }));
    }
    if (this.summary?.creditBalance && this.summary.creditBalance > 0) {
      return [{ currency: this.summary.currency, amount: this.summary.creditBalance }];
    }
    return [];
  }

  // ─── Formatters ───────────────────────────────────────────────────────────

  formatDateTime(val: string | null | undefined): string {
    if (!val) return '—';
    const d = new Date(val);
    return isNaN(d.getTime())
      ? '—'
      : d.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
  }

  formatDateOnly(val: string | null | undefined): string {
    if (!val) return '—';
    const d = new Date(val);
    return isNaN(d.getTime())
      ? '—'
      : d.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        });
  }

  formatTimeOnly(val: string | null | undefined): string {
    if (!val) return '—';
    const d = new Date(val);
    return isNaN(d.getTime())
      ? '—'
      : d.toLocaleTimeString('en-GB', {
          hour: '2-digit',
          minute: '2-digit',
        });
  }

  getLessonStatusClass(status: number): string {
    switch (status) {
      case 1:
        return 'bg-light-primary text-primary';
      case 2:
        return 'bg-light-info text-info';
      case 3:
        return 'bg-light-success text-success';
      case 4:
        return 'bg-light-warning text-warning';
      case 5:
        return 'bg-light-danger text-danger';
      default:
        return 'bg-light text-muted';
    }
  }

  getAttendanceClass(attendance: number | null | undefined): string {
    if (attendance === 1) return 'bg-light-success text-success';
    if (attendance === 2) return 'bg-light-danger text-danger';
    return 'bg-light-primary text-primary';
  }

  // ─── School Switching ─────────────────────────────────────────────────────

  selectSchool(schoolId: string): void {
    if (!this.rawToken || schoolId === this.selectedSchoolId) return;
    this.selectedSchoolId = schoolId;
    this.switchingSchool = true;
    this.loadPortalData(this.rawToken, schoolId);
  }

  // ─── Data Loading ─────────────────────────────────────────────────────────

  loadPortalData(token: string, schoolId?: string): void {
    this.portalSvc.verifyToken(token, schoolId).subscribe({
      next: (res) => {
        this.student = res.student;
        this.school = res.school || res.student?.school || null;
        this.summary = res.summary;
        this.breakdown = res.breakdown || null;
        this.bookings = res.bookings || [];
        this.classes = res.classes || [];
        this.transactions = res.transactions || [];
        this.boxBookings = res.boxBookings || [];
        this.rentals = res.rentals || [];
        this.availableSchools = res.availableSchools || [];

        if (!this.selectedSchoolId && this.school?.id) {
          this.selectedSchoolId = this.school.id;
        }

        this.state = 'ready';
        this.switchingSchool = false;
      },
      error: () => {
        this.state = 'error';
        this.switchingSchool = false;
      },
    });
  }

  ngOnInit(): void {
    this.rawToken = this.route.snapshot.paramMap.get('token');
    if (!this.rawToken) {
      this.state = 'error';
      return;
    }
    this.loadPortalData(this.rawToken);
  }
}
